/**
 * React glue for the identity module.
 *
 * <IdentityProvider> calls getOrCreateGuestSession() on mount (so module
 * load remains side-effect-free), subscribes to the vanilla session store,
 * and also bridges @solana/wallet-adapter-react's useWallet() so a wallet
 * connect/disconnect propagates into our WalletSlot automatically.
 *
 * Consumers use useIdentity() to get { guest, wallet, attachWallet,
 * detachWallet }. Wallet is additive — guest is always present.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";

import { useWallet } from "@solana/wallet-adapter-react";

import type { GuestSession, WalletSlot } from "@solblox/shared";

import {
  EMPTY_WALLET,
  attachWallet as attachWalletCore,
  detachWallet as detachWalletCore,
  getOrCreateGuestSession,
  getWalletSlot,
  subscribe,
} from "./session.js";

export interface IdentityContextValue {
  guest: GuestSession;
  wallet: WalletSlot;
  attachWallet: (pubkey: string, adapter?: string) => void;
  detachWallet: () => void;
}

const IdentityContext = createContext<IdentityContextValue | null>(null);

export interface IdentityProviderProps {
  children: ReactNode;
}

export function IdentityProvider({ children }: IdentityProviderProps): ReactElement {
  // Lazy init so we never touch storage during render commit on the server
  // and so each Provider mount produces stable initial state.
  const [guest, setGuest] = useState<GuestSession>(() => getOrCreateGuestSession());
  const [wallet, setWallet] = useState<WalletSlot>(() => getWalletSlot());

  // Bridge wallet adapter → identity store.
  const walletAdapter = useWallet();
  const { publicKey, wallet: adapterWallet } = walletAdapter;

  useEffect(() => {
    const unsubscribe = subscribe((snap) => {
      setGuest(snap.guest);
      setWallet(snap.wallet);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (publicKey) {
      const adapterName: string = adapterWallet?.adapter?.name ?? "";
      attachWalletCore(publicKey.toBase58(), adapterName);
    } else if (wallet.pubkey !== "") {
      detachWalletCore();
    }
    // wallet.pubkey is intentionally not a dep — we only want this to fire
    // when the wallet-adapter state changes, not after our own store
    // update triggers a re-render. The subscribe() path keeps `wallet`
    // fresh for the rest of the tree.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicKey, adapterWallet]);

  const attachWallet = useCallback((pubkey: string, adapter: string = ""): void => {
    attachWalletCore(pubkey, adapter);
  }, []);

  const detachWallet = useCallback((): void => {
    detachWalletCore();
  }, []);

  const value = useMemo<IdentityContextValue>(
    () => ({ guest, wallet, attachWallet, detachWallet }),
    [guest, wallet, attachWallet, detachWallet],
  );

  return <IdentityContext.Provider value={value}>{children}</IdentityContext.Provider>;
}

/**
 * Read the current identity. Throws if called outside <IdentityProvider>
 * so misuse is loud instead of returning a silent default.
 */
export function useIdentity(): IdentityContextValue {
  const ctx = useContext(IdentityContext);
  if (ctx === null) {
    throw new Error("useIdentity() must be used inside <IdentityProvider>");
  }
  return ctx;
}

/** Default empty wallet exported for consumers that need a placeholder. */
export { EMPTY_WALLET };
